import api from './api';
import { Project } from '../types/api';
import { API_BASE_URL } from '../config';

export interface ProjectFolder {
  id: number;
  project_id: number;
  parent_folder_id?: number;
  name: string;
  description?: string;
  sort_order: number;
  is_system_folder: boolean;
  created_at: string;
  updated_at: string;
  sub_folders: ProjectFolder[];
  document_count: number;
  documents: ProjectDocument[];  // Dokumente hinzufügen
}

export interface ProjectDocument {
  id: number;
  project_id: number;
  folder_id?: number;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  uploaded_by: number;
  created_at: string;
  updated_at: string;
}

export const getProjects = async (): Promise<Project[]> => {
  const response = await api.get<Project[]>('/projects');
  return response.data;
};

export const getProject = async (id: number): Promise<Project> => {
  const response = await api.get<Project>(`/projects/${id}`);
  return response.data;
};

export const createProject = async (data: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project> => {
  const response = await api.post<Project>('/projects', data);
  return response.data;
};

export const updateProject = async (id: number, data: Partial<Omit<Project, 'id' | 'created_at' | 'updated_at'>>): Promise<Project> => {
  const response = await api.put<Project>(`/projects/${id}`, data);
  return response.data;
};

export const deleteProject = async (id: number): Promise<void> => {
  await api.delete(`/projects/${id}`);
};

export const uploadDocument = async (projectId: number, file: File): Promise<ProjectDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<ProjectDocument>(`/projects/${projectId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getProjectDocuments = async (projectId: number): Promise<ProjectDocument[]> => {
  const response = await api.get<ProjectDocument[]>(`/projects/${projectId}/documents`);
  return response.data;
};

export const deleteDocument = async (projectId: number, documentId: number): Promise<void> => {
  await api.delete(`/projects/${projectId}/documents/${documentId}`);
};

export const getDocumentViewUrl = (projectId: number, documentId: number): string => {
  const baseUrl = API_BASE_URL;
  const token = localStorage.getItem('token');
  const url = `${baseUrl}/projects/${projectId}/documents/${documentId}/view`;
  
  if (token) {
    return `${url}?token=${encodeURIComponent(token)}`;
  }
  
  return url;
};

export const isImageFile = (filename: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return imageExtensions.includes(extension);
};

export const isPdfFile = (filename: string): boolean => {
  return filename.toLowerCase().endsWith('.pdf');
};

// ============================================================================
// ORDNER-API FUNKTIONEN
// ============================================================================

export const createFolder = async (projectId: number, folderData: {
  name: string;
  description?: string;
  parent_folder_id?: number;
  sort_order?: number;
}): Promise<ProjectFolder> => {
  const response = await api.post<ProjectFolder>(`/projects/${projectId}/folders`, folderData);
  return response.data;
};

export const getProjectFolders = async (projectId: number): Promise<{
  folders: ProjectFolder[];
  total: number;
}> => {
  const response = await api.get<{ folders: ProjectFolder[]; total: number }>(`/projects/${projectId}/folders`);
  return response.data;
};

export const updateFolder = async (projectId: number, folderId: number, folderData: {
  name?: string;
  description?: string;
  sort_order?: number;
}): Promise<ProjectFolder> => {
  const response = await api.put<ProjectFolder>(`/projects/${projectId}/folders/${folderId}`, folderData);
  return response.data;
};

export const deleteFolder = async (projectId: number, folderId: number): Promise<void> => {
  await api.delete(`/projects/${projectId}/folders/${folderId}`);
};

export const uploadDocumentToFolder = async (projectId: number, folderId: number, file: File): Promise<ProjectDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<ProjectDocument>(`/projects/${projectId}/folders/${folderId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// ============================================================================
// FINANCIAL OPERATIONS - Admin and Bauleiter only
// ============================================================================

export interface ContractAmountResponse {
  message: string;
  project_id: number;
  old_amount: number;
  added_amount?: number;
  subtracted_amount?: number;
  new_amount: number;
  description: string;
}

export interface ContractHistoryEntry {
  id: number;
  project_id: number;
  transaction_type: 'initial' | 'increase' | 'decrease';
  amount: number;
  previous_amount: number;
  new_amount: number;
  description: string;
  created_by_id: number;
  created_at: string;
  created_by_name: string;
}

export const increaseContractAmount = async (
  projectId: number, 
  amount: number, 
  description: string
): Promise<ContractAmountResponse> => {
  const formData = new FormData();
  formData.append('amount', amount.toString());
  formData.append('description', description);
  
  const response = await api.post<ContractAmountResponse>(
    `/projects/${projectId}/increase-contract-amount`, 
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const subtractInvoicedAmount = async (
  projectId: number, 
  amount: number, 
  description: string
): Promise<ContractAmountResponse> => {
  const formData = new FormData();
  formData.append('amount', amount.toString());
  formData.append('description', description);
  
  const response = await api.post<ContractAmountResponse>(
    `/projects/${projectId}/subtract-invoiced-amount`, 
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const getContractHistory = async (projectId: number): Promise<ContractHistoryEntry[]> => {
  const response = await api.get<ContractHistoryEntry[]>(`/projects/${projectId}/contract-history`);
  return response.data;
}; 